/**
 * POCTimelineView.jsx
 * Calendar/Timeline view for Plan of Care interventions and events
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar, Clock, ChevronLeft, ChevronRight, CalendarDays, List,
  Users, Activity, Heart, Pill, AlertTriangle, CheckCircle, 
  Loader2, RefreshCw, Filter, Eye, Play, FileText, Stethoscope
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Discipline configuration with colors
const DISCIPLINES = {
  rn: { label: 'RN', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  lpn: { label: 'LPN', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' },
  social_work: { label: 'Social Work', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50' },
  chaplain: { label: 'Chaplain', color: 'bg-indigo-500', textColor: 'text-indigo-700', bgLight: 'bg-indigo-50' },
  aide: { label: 'Aide', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  volunteer: { label: 'Volunteer', color: 'bg-pink-500', textColor: 'text-pink-700', bgLight: 'bg-pink-50' },
  physician: { label: 'Physician', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
  dietitian: { label: 'Dietitian', color: 'bg-teal-500', textColor: 'text-teal-700', bgLight: 'bg-teal-50' },
  physical_therapy: { label: 'PT', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  occupational_therapy: { label: 'OT', color: 'bg-sky-500', textColor: 'text-sky-700', bgLight: 'bg-sky-50' },
  speech_therapy: { label: 'SLP', color: 'bg-cyan-500', textColor: 'text-cyan-700', bgLight: 'bg-cyan-50' }
};

// Event type colors
const EVENT_COLORS = {
  intervention: 'border-l-emerald-500',
  idg_review: 'border-l-indigo-500',
  visit: 'border-l-blue-500',
  recertification: 'border-l-red-500'
};

export default function POCTimelineView({ pocId, patientName }) {
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // week, month, list
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [idgReviews, setIdgReviews] = useState(null);
  const [nextIdgReview, setNextIdgReview] = useState(null);
  
  // Load timeline data
  useEffect(() => {
    if (pocId) {
      loadTimeline();
      loadIdgReviews();
    }
  }, [pocId, viewMode, weekOffset, monthOffset]);
  
  const loadTimeline = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hospice_token');
      let url = `${API_URL}/api/admission-poc/${pocId}/timeline`;
      
      if (viewMode === 'week') {
        url = `${API_URL}/api/admission-poc/${pocId}/timeline/week?week_offset=${weekOffset}`;
      } else if (viewMode === 'month') {
        url = `${API_URL}/api/admission-poc/${pocId}/timeline/month?month_offset=${monthOffset}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeline(data);
      }
    } catch (error) {
      console.error('Failed to load timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };
  
  const loadIdgReviews = async () => {
    try {
      const token = localStorage.getItem('hospice_token');
      
      const [reviewsRes, nextRes] = await Promise.all([
        fetch(`${API_URL}/api/admission-poc/${pocId}/idg-reviews`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admission-poc/${pocId}/next-idg-review`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (reviewsRes.ok) setIdgReviews(await reviewsRes.json());
      if (nextRes.ok) setNextIdgReview(await nextRes.json());
    } catch (error) {
      console.error('Failed to load IDG reviews:', error);
    }
  };
  
  // Filter events by discipline
  const filteredEvents = useMemo(() => {
    if (!timeline?.timeline) return [];
    if (selectedDiscipline === 'all') return timeline.timeline;
    return timeline.timeline.filter(e => 
      e.discipline === selectedDiscipline || e.type === 'idg_review' || e.type === 'recertification'
    );
  }, [timeline, selectedDiscipline]);
  
  // Group events by date for calendar view
  const calendarData = useMemo(() => {
    if (!timeline?.calendar) return {};
    if (selectedDiscipline === 'all') return timeline.calendar;
    
    const filtered = {};
    Object.entries(timeline.calendar).forEach(([date, events]) => {
      const filteredEvents = events.filter(e => 
        e.discipline === selectedDiscipline || e.type === 'idg_review' || e.type === 'recertification'
      );
      if (filteredEvents.length > 0) {
        filtered[date] = filteredEvents;
      }
    });
    return filtered;
  }, [timeline, selectedDiscipline]);
  
  // Generate week days for calendar
  const weekDays = useMemo(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7)); // Monday start
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        isToday: date.toISOString().split('T')[0] === today.toISOString().split('T')[0]
      });
    }
    return days;
  }, [weekOffset]);
  
  // Get events for a specific date
  const getEventsForDate = (date) => {
    return calendarData[date] || [];
  };
  
  // Navigate functions
  const goToPrevious = () => {
    if (viewMode === 'week') setWeekOffset(prev => prev - 1);
    else setMonthOffset(prev => prev - 1);
  };
  
  const goToNext = () => {
    if (viewMode === 'week') setWeekOffset(prev => prev + 1);
    else setMonthOffset(prev => prev + 1);
  };
  
  const goToToday = () => {
    setWeekOffset(0);
    setMonthOffset(0);
  };
  
  // Get event icon
  const getEventIcon = (type) => {
    switch (type) {
      case 'intervention': return <Activity className="h-3 w-3" />;
      case 'idg_review': return <Users className="h-3 w-3" />;
      case 'visit': return <Stethoscope className="h-3 w-3" />;
      case 'recertification': return <FileText className="h-3 w-3" />;
      default: return <Clock className="h-3 w-3" />;
    }
  };
  
  if (loading && !timeline) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        <span className="ml-2">Loading timeline...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="poc-timeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-teal-500" />
            POC Timeline View
          </h2>
          <p className="text-slate-500 text-sm">
            {patientName && `${patientName} - `}Interventions, visits, and IDG reviews
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Discipline Filter */}
          <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Disciplines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Disciplines</SelectItem>
              {Object.entries(DISCIPLINES).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg">
            <Button 
              variant={viewMode === 'week' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('week')}
            >
              <CalendarDays className="h-4 w-4 mr-1" />
              Week
            </Button>
            <Button 
              variant={viewMode === 'month' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('month')}
            >
              <Calendar className="h-4 w-4 mr-1" />
              Month
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
          </div>
          
          <Button variant="outline" size="sm" onClick={loadTimeline}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {timeline?.summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card className="bg-slate-50 dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total Events</p>
                  <p className="text-2xl font-bold">{timeline.summary.total_events}</p>
                </div>
                <Calendar className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${timeline.summary.overdue > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Overdue</p>
                  <p className={`text-2xl font-bold ${timeline.summary.overdue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {timeline.summary.overdue}
                  </p>
                </div>
                <AlertTriangle className={`h-8 w-8 ${timeline.summary.overdue > 0 ? 'text-red-400' : 'text-green-400'}`} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 dark:bg-blue-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Today</p>
                  <p className="text-2xl font-bold text-blue-600">{timeline.summary.today}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-indigo-50 dark:bg-indigo-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">IDG Reviews</p>
                  <p className="text-2xl font-bold text-indigo-600">{timeline.summary.idg_reviews_upcoming}</p>
                </div>
                <Users className="h-8 w-8 text-indigo-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-50 dark:bg-emerald-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Interventions Due</p>
                  <p className="text-2xl font-bold text-emerald-600">{timeline.summary.interventions_due}</p>
                </div>
                <Activity className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Next IDG Review Alert */}
      {nextIdgReview?.next_review && (
        <Alert className="bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20">
          <Users className="h-4 w-4 text-indigo-600" />
          <AlertDescription className="text-indigo-800 dark:text-indigo-200">
            <div className="flex items-center justify-between">
              <div>
                <strong>Next IDG Review:</strong> {new Date(nextIdgReview.next_review.scheduled_date).toLocaleDateString()} 
                {nextIdgReview.next_review.is_overdue && (
                  <Badge variant="destructive" className="ml-2">Overdue</Badge>
                )}
                {!nextIdgReview.next_review.is_overdue && nextIdgReview.next_review.days_until !== undefined && (
                  <span className="ml-2 text-sm">({nextIdgReview.next_review.days_until} days)</span>
                )}
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setSelectedEvent({ type: 'idg_agenda', data: nextIdgReview })}
              >
                <Eye className="h-4 w-4 mr-1" />
                View Agenda
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPrevious}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goToNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="text-sm text-slate-500">
          {timeline?.timeline_range && (
            <>
              {new Date(timeline.timeline_range.start).toLocaleDateString()} - {new Date(timeline.timeline_range.end).toLocaleDateString()}
            </>
          )}
        </div>
      </div>

      {/* Calendar/List View */}
      {viewMode === 'week' && (
        <Card>
          <CardContent className="p-0">
            {/* Week Header */}
            <div className="grid grid-cols-7 border-b">
              {weekDays.map((day) => (
                <div 
                  key={day.date} 
                  className={`p-3 text-center border-r last:border-r-0 ${
                    day.isToday ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                  }`}
                >
                  <p className="text-xs text-slate-500">{day.dayName}</p>
                  <p className={`text-lg font-bold ${day.isToday ? 'text-teal-600' : ''}`}>
                    {day.dayNum}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Week Events */}
            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDays.map((day) => {
                const dayEvents = getEventsForDate(day.date);
                return (
                  <div 
                    key={day.date} 
                    className={`p-2 border-r last:border-r-0 ${
                      day.isToday ? 'bg-teal-50/50 dark:bg-teal-900/10' : ''
                    }`}
                  >
                    <ScrollArea className="h-[380px]">
                      <div className="space-y-1">
                        {dayEvents.map((event, idx) => {
                          const discipline = DISCIPLINES[event.discipline];
                          return (
                            <div
                              key={event.id || idx}
                              className={`p-2 rounded text-xs cursor-pointer hover:opacity-80 border-l-2 ${
                                EVENT_COLORS[event.type] || 'border-l-gray-500'
                              } ${discipline?.bgLight || 'bg-slate-50'} ${
                                event.is_overdue ? 'ring-1 ring-red-300' : ''
                              }`}
                              onClick={() => setSelectedEvent(event)}
                            >
                              <div className="flex items-center gap-1">
                                {getEventIcon(event.type)}
                                <span className="font-medium truncate">{event.title}</span>
                              </div>
                              {event.discipline && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-[10px] mt-1 ${discipline?.textColor || ''}`}
                                >
                                  {discipline?.label || event.discipline}
                                </Badge>
                              )}
                              {event.is_overdue && (
                                <Badge variant="destructive" className="text-[10px] mt-1 ml-1">
                                  Overdue
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                        {dayEvents.length === 0 && (
                          <p className="text-center text-slate-300 text-xs py-4">No events</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Events</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {filteredEvents.map((event, idx) => {
                  const discipline = DISCIPLINES[event.discipline];
                  const eventDate = new Date(event.date);
                  return (
                    <div
                      key={event.id || idx}
                      className={`p-3 rounded-lg border-l-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${
                        EVENT_COLORS[event.type] || 'border-l-gray-500'
                      } ${event.is_overdue ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded ${discipline?.bgLight || 'bg-slate-100'}`}>
                            {getEventIcon(event.type)}
                          </div>
                          <div>
                            <p className="font-medium">{event.title}</p>
                            <p className="text-xs text-slate-500">
                              {eventDate.toLocaleDateString()} at {eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {event.discipline && (
                            <Badge variant="outline" className={discipline?.textColor || ''}>
                              {discipline?.label || event.discipline}
                            </Badge>
                          )}
                          <Badge variant={event.status === 'completed' ? 'default' : 'secondary'}>
                            {event.status || 'scheduled'}
                          </Badge>
                          {event.is_overdue && (
                            <Badge variant="destructive">Overdue</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Discipline Legend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Discipline Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(DISCIPLINES).map(([key, { label, color }]) => (
              <div key={key} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded ${color}`}></div>
                <span className="text-xs text-slate-600">{label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1 ml-4">
              <div className="w-3 h-3 rounded bg-indigo-500"></div>
              <span className="text-xs text-slate-600">IDG Review</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-500"></div>
              <span className="text-xs text-slate-600">Recertification</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && getEventIcon(selectedEvent.type)}
              {selectedEvent?.type === 'idg_agenda' ? 'IDG Review Agenda' : selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent?.type === 'idg_agenda' && selectedEvent?.data?.agenda && (
            <div className="space-y-4">
              {selectedEvent.data.agenda.sections?.map((section, idx) => (
                <div key={idx}>
                  <h4 className="font-semibold text-sm text-slate-700">{section.title}</h4>
                  {Array.isArray(section.items) && (
                    <ul className="text-sm text-slate-600 list-disc list-inside mt-1">
                      {section.items.slice(0, 5).map((item, i) => (
                        <li key={i}>{typeof item === 'string' ? item : item.description || item.goal || JSON.stringify(item)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {selectedEvent?.type !== 'idg_agenda' && selectedEvent && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Type</Label>
                  <p className="font-medium capitalize">{selectedEvent.type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Badge variant={selectedEvent.status === 'completed' ? 'default' : 'secondary'}>
                    {selectedEvent.status || 'scheduled'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Scheduled Date</Label>
                <p className="font-medium">
                  {selectedEvent.date && new Date(selectedEvent.date).toLocaleString()}
                </p>
              </div>
              
              {selectedEvent.discipline && (
                <div>
                  <Label className="text-xs text-slate-500">Discipline</Label>
                  <Badge variant="outline" className={DISCIPLINES[selectedEvent.discipline]?.textColor || ''}>
                    {DISCIPLINES[selectedEvent.discipline]?.label || selectedEvent.discipline}
                  </Badge>
                </div>
              )}
              
              {selectedEvent.frequency && (
                <div>
                  <Label className="text-xs text-slate-500">Frequency</Label>
                  <p className="font-medium capitalize">{selectedEvent.frequency.replace('_', ' ')}</p>
                </div>
              )}
              
              {selectedEvent.is_overdue && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    This item is overdue and requires immediate attention.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedEvent(null)}>
              Close
            </Button>
            {selectedEvent?.type === 'intervention' && selectedEvent?.status === 'pending' && (
              <Button className="bg-teal-600 hover:bg-teal-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Complete
              </Button>
            )}
            {selectedEvent?.type === 'idg_review' && selectedEvent?.status === 'scheduled' && (
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                <Play className="h-4 w-4 mr-2" />
                Start Review
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
